import { Button } from "@material-ui/core";
import ErrorMessage from "components/error-message";
import LottoLocoBanner from "components/lotto-loco-banner";
import LottoLocoModal from "components/lotto-loco-modal";
import useIdResource from "hooks/use-id-resource";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { RootState, UserInfo } from "store/reducer";
import {
  TastingAnswer,
  answersSelectors,
} from "store/reducers/answers/reducer";
import { TastingEvent } from "store/reducers/events/reducer";
import TastingAnswers from "../../components/tasting-answers";
import { answers, events } from "../../store";
import {
  Container,
  AllowEditField,
  CircularProgress,
  Section,
} from "./Tasting.styles";

export type TastingProps = {
  baseURL: string;
};

const Tasting = ({ baseURL }: TastingProps) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const dispatch = useDispatch();
  const user = useSelector<RootState, UserInfo | null>((s) => s?.auth?.user);

  // useIdResource owns the load + selectors + showLoading derivation. The
  // staleness guard inside the id-resource reducer prevents the A → B → A
  // race that used to leave this screen stuck on the spinner.
  const {
    item: userAnswers,
    error: answersError,
    showLoading: displayAnswersLoading,
  } = useIdResource<TastingAnswer>({
    id,
    selectors: answersSelectors,
    load: useCallback((eventId: string) => answers.loadItem(eventId), []),
  });

  const tastingEvent = useSelector<RootState, TastingEvent>(
    (s) => s?.events?.items?.find((i) => i.id === id) || ({} as TastingEvent)
  );

  const bartender = tastingEvent.bartender;
  const owner = tastingEvent.owner;
  const editingAllowed = Boolean(tastingEvent.editingAllowed);
  const lottoLocoEnabled = Boolean(tastingEvent.lottoLocoEnabled);
  const isBartender = bartender === user?.email;
  const canEdit = owner === user?.email;
  // Bartenders hold the correct answers — they should never randomly fill them.
  const showLottoLoco =
    lottoLocoEnabled && editingAllowed && !isBartender && Boolean(userAnswers);

  const [lottoOpen, setLottoOpen] = useState(false);

  const handleConfirmLotto = (picks: (string | null)[]) => {
    setLottoOpen(false);
    if (!userAnswers) return;
    const totalRounds =
      userAnswers.beers?.length ?? userAnswers.rounds ?? picks.length;
    const baseChanges =
      userAnswers.changes ?? new Array(totalRounds).fill(0);
    const baseLotto =
      userAnswers.lottoLoco ?? new Array(totalRounds).fill(false);
    const beers = picks.slice(0, totalRounds);
    const lottoLoco = Array(totalRounds)
      .fill(false)
      .map((_, i) => (i < picks.length ? true : Boolean(baseLotto[i])));
    const changes = baseChanges.map((c, i) =>
      i < picks.length ? (c ?? 0) + 1 : c
    );
    dispatch(
      answers.update(id, {
        ...userAnswers,
        beers,
        changes,
        lottoLoco,
      } as TastingAnswer)
    );
  };

  const handleAllowEditing = () => {
    const message = editingAllowed
      ? "Are you sure you want to stop editing answers?"
      : "Are you sure you want to allow editing answers?";
    if (window.confirm(message)) {
      dispatch(events.allowEdit(id, !editingAllowed));
    }
  };

  const handleClickItemAtIndex = (index: number) => {
    index !== undefined && history.push(`${baseURL}/round/${index + 1}`);
  };

  return (
    <Container id="tasting" disableGutters>
      {displayAnswersLoading ? (
        <CircularProgress />
      ) : (
        <Section>
          {answersError && (
            <ErrorMessage error={answersError}>
              <Button color="inherit" onClick={() => dispatch(answers.loadItem(id))}>
                Retry
              </Button>
            </ErrorMessage>
          )}
          {canEdit && (
            <AllowEditField
              onChange={handleAllowEditing}
              value={editingAllowed}
            />
          )}
          {showLottoLoco && (
            <LottoLocoBanner onPlay={() => setLottoOpen(true)} />
          )}
          {userAnswers && (
            <TastingAnswers
              answers={userAnswers}
              showForBartender={isBartender}
              editingAllowed={editingAllowed}
              reminderMode={tastingEvent?.reminderMode ?? "off"}
              onClickItemAtIndex={handleClickItemAtIndex}
            />
          )}
        </Section>
      )}
      {showLottoLoco && (
        <LottoLocoModal
          open={lottoOpen}
          rounds={Number(tastingEvent?.rounds ?? userAnswers?.rounds ?? 0)}
          beers={(tastingEvent?.beers ?? []).filter(
            (b): b is string => Boolean(b)
          )}
          onCancel={() => setLottoOpen(false)}
          onConfirm={handleConfirmLotto}
        />
      )}
    </Container>
  );
};
export default Tasting;
